"use client";

import { useState, useEffect, useRef } from "react";
import { AnimatedCounter } from "@/components/animated-counter";
import type { ConferenceStatsSectionData } from "@/types/cms";
import { useSectionTracking } from "@/lib/analytics/use-section-tracking";

interface ConferenceStatsProps {
  data: ConferenceStatsSectionData;
}

export function ConferenceStats({ data }: ConferenceStatsProps) {
  const sectionRef = useSectionTracking(
    "Conference Stats Section",
    "conference-stats",
  );
  const [countersVisible, setCountersVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setCountersVisible(true);
          }
        });
      },
      { threshold: 0.3 },
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={(el: HTMLDivElement | null) => {
        statsRef.current = el;
        (sectionRef as React.MutableRefObject<HTMLElement | null>).current = el;
      }}
      className="w-full py-12 md:py-16 px-6"
    >
      <div className="max-w-5xl mx-auto">
        <div className="bg-neutral-950 border border-white/10 rounded-2xl md:rounded-3xl p-8 md:p-12 lg:p-16">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-center">
            {/* Left Side - Statement */}
            <div className="flex-1 text-center lg:text-left">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                {data.title}{" "}
                <span className="text-[#D4A84B]">{data.highlightedText}</span>
              </h2>
            </div>

            {/* Right Side - Stats */}
            <div className="w-full lg:w-auto lg:min-w-[280px]">
              <div className="flex flex-row lg:flex-col gap-3 md:gap-4">
                {data.statItems.map((stat) => (
                  <div
                    key={stat.id}
                    className="flex-1 p-4 md:p-6 bg-white/5 border border-white/10 rounded-xl md:rounded-2xl hover:bg-white/[0.07] transition-all"
                  >
                    <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-1">
                      <AnimatedCounter
                        end={parseFloat(stat.value)}
                        visible={countersVisible}
                        suffix={stat.suffix}
                        decimals={stat.decimals}
                      />
                    </div>
                    <p className="text-white/70 text-xs md:text-sm lg:text-base font-medium">
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
